// =========================================================================
// The message the SECOND registered device signs to approve a payout card
// (N-068, the operator's "2 device", 22 Oct 2026).
//
// A payout is signed on-chain by the key that holds the float — the
// proposer's own device, as a rule. With one device on the desk that is the
// whole ceremony. Once a second device is registered (admin.custody_signers,
// two unrevoked rows on the chain) a payout also needs that other device's
// word: a short message naming the card, signed on the Ledger, recovered by
// the Worker and accepted only from a registered device that is neither the
// card's signer nor its proposer. The orchestrator sends nothing without it.
//
// Plain text, one fact per line, so the Ledger shows it and a human reads
// it. TIP-191 personal message, as signer/src/key-approval.mjs.
// =========================================================================

export const CARD_APPROVAL_DOMAIN = 'DubaiOTC payout approval v1';
/**
 * Still a TIP-191 message, which the TRON app shows only as a hash (the
 * operator, 22 Oct 2026: no blind signing, no message signing). The
 * two-device switch (db/0088) refuses to turn on while this is true; the
 * approval is converted to a shown, never-broadcastable transfer — as the
 * key proof was (key-approval.mjs) — before it ever is.
 */
export const CARD_APPROVAL_IS_MESSAGE = true;
/** How long a signed approval message stands before the Worker refuses it as stale. */
export const CARD_APPROVAL_WINDOW_MS = 10 * 60_000;

const TXID = /^[0-9a-f]{64}$/;
const LINE = /^([a-z ]+) (.+)$/;

/**
 * @param {{ txid: string, at: number }} i  the card's transaction id (64 hex) and when the message was made
 * @returns {string}
 */
export function buildCardApprovalMessage({ txid, at }) {
  if (typeof txid !== 'string' || !TXID.test(txid.toLowerCase())) throw new Error('buildCardApprovalMessage: txid must be 64 hex characters');
  if (!Number.isFinite(at)) throw new Error('buildCardApprovalMessage: at must be epoch ms');
  return `${CARD_APPROVAL_DOMAIN}\ntxid ${txid.toLowerCase()}\nat ${new Date(at).toISOString()}`;
}

/**
 * The message read back, or null when it is not one of ours: the domain
 * line, then exactly the two facts, nothing else.
 * @returns {{ txid: string, at: number } | null}
 */
export function parseCardApprovalMessage(text) {
  if (typeof text !== 'string') return null;
  const lines = text.split('\n');
  if (lines.length !== 3 || lines[0] !== CARD_APPROVAL_DOMAIN) return null;
  const facts = {};
  for (const line of lines.slice(1)) {
    const m = LINE.exec(line);
    if (!m) return null;
    facts[m[1]] = m[2];
  }
  const txid = facts.txid;
  const at = Date.parse(facts.at ?? '');
  if (typeof txid !== 'string' || !TXID.test(txid)) return null;
  if (!Number.isFinite(at) || new Date(at).toISOString() !== facts.at) return null;
  return { txid, at };
}
